// QEW Field Access and the Dashboard landing.
// Words: STEP3_SIGNIN_APPROVED.md (2026-09-29). Mockup: MOCKUP_SIGNIN_APPROVED.html.
// Dashboard top block and Training card: QEW_DASHBOARD_WORDS.md (2026-10-06).
//
// Three doors in:
// * Bag tag: a claimed tag goes straight to "Is this you?".
// * Asset: Field Access on the guest page opens the gate. Scan the bag tag or type the ID.
// * Cold: the app opened with a QEW still signed in goes to the Dashboard.
// The visit ends on Sign Out or 1 hour with no activity (access.ts).
// LOTO card and Report LOTO Date: QEW_DASHBOARD_WORDS.md, screens 1 to 5 (2026-10-06).
// My Tasks (Tier 1, 2, 3 collapsible lists, read only): screens 1 and 7.
// Glove card: GLOVES_REWORK_WORDS.md, MOCKUP_GLOVES_REWORK.html (approved 2026-10-06).
// Size on the card, guided Gloves Arrived, one-tap Place In Service, Remove for Damage or Lost.
// Opens once Nodes 1 to 3 are done. The app retires expired pairs nightly.
// Field Access gate: WALKTHROUGH_FIXES_M3.md. Cancel goes back one step. The camera waits for a tap.
// Resources: RESOURCES_WORDS.md, MOCKUP_RESOURCES.html (approved 2026-10-06).
// SOP Library for review, never qualifies. Every open is logged. Inspection program sheets.
// Asset page and Pick a Task: QEW_DASHBOARD_WORDS.md (2026-10-06), JSP_ADJUSTMENTS.md items 1 to 4,
// MOCKUP_JSP_APPROVED.html (2026-10-08). Robert, 2026-10-09: from an asset the name check opens the
// QEW page, never the Dashboard. The Dashboard opens from the yellow bag tag, or the My Dashboard button
// on the QEW page. Review SOP is the only way to qualify.
//
// The component owns its own box and events (data-fa). It never uses data-act or <form>.

import { keepAdminBack } from "./admin-way-back";
import { clockOf, type EewpAsk } from "./eewp";
import "./field-access.css";
import { isSessionExpired } from "./access";
import type { SignInClaims } from "./badge";
import type { TagView } from "./claim";
import { OTHER_SIGN_IN, dashboardStatus, isQewSignIn, nameCheckOf, typedQewId } from "./field-access";
import { callServer, currentBadge, signInWithBadgeToken, signOutNow } from "./firebase";
import { isAdminBadgeId, readQrLink } from "./qr-link";
import { startQrScanner, type QrScanner } from "./qr-scanner";
import { GLOVE_CLASS_LINE, GLOVE_SIZES, REMOVAL_REASONS, arrivalProblem, gloveCardOf, testDateProblem, type GlovePair, type GloveView, type LastInspection, type RetiredNote } from "./gloves";
import { lotoCardOf, lotoReportProblem, type LotoPerson } from "./loto";
import { myTasksCard, myTasksCardHtml, type TaskAuthorization, type TaskDoc } from "./my-tasks";
import { qewTrainingCard, qewTrainingCardHtml } from "./qew-training";
import { INSPECTION_PROGRAMS, LIBRARY_FOOT, REVIEW_LINE, findSop, reviewHtml, type SopEntry, type SopLibrary } from "./resources";
import { dateInSiteTime } from "./training-server";
import { cardFor, sopFor, sopReadHtml, workHtml, workLoadingHtml, type AssetWork, type SopReadState } from "./asset-work-screens";
import { mountJsp, type JspHandle } from "./jsp-page";
import { LEFT_AS_LABEL, REPAIR_NOTE_PROMPT, type CloseCard } from "./jsp-close";
import { chainOf, hashOf, moveTo, pageFromHash, parentOf, type Page } from "./nav-history";
import type { JspDraft } from "./jsp";
import { qualifyingHtml, sopAckOf, sopAckPayload, type SopAck } from "./task-pick";
import type { NodeId } from "./training";

interface MyDashboard extends LotoPerson {
  /** EEWP rev 3: permits that name this QEW as second QEW, waiting on the answer. */
  eewpAsks?: EewpAsk[];
  /** Item 5: How did you leave it? */
  closeCard?: CloseCard | null;
  badgeId: string;
  holderName: string;
  siteName: string;
  isActive: boolean;
  records: { nodeId: string; completedOn: string; expiresOn: string | null }[];
  tasks: TaskDoc[];
  taskAuthorizations: TaskAuthorization[];
  taskAuthCycleDays: number | null;
  gloveSize: string | null;
  gloves: GlovePair | null;
  lastGloveInspection: LastInspection | null;
  lastRetiredGloves: RetiredNote | null;
}

interface GloveArrive {
  data: MyDashboard;
  /** The pair this replaces, inside its 30-day window. */
  replacing: string | null;
  /** Step 1. Null until he answers. */
  sealed: boolean | null;
  testedOn: string;
  firstUseMode: "today" | "earlier" | null;
  firstUse: string;
  error: string;
  busy: boolean;
}

interface GloveCardView {
  data: MyDashboard;
  /** The size button picked on the card. */
  pickSize: string;
  editingSize: boolean;
  busy: boolean;
  error: string;
}

export type FieldAccessStart =
  | { kind: "tag"; view: TagView }
  | { kind: "asset"; assetId: string; siteName: string }
  | { kind: "dashboard" };

const LAST_ACTIVITY_KEY = "safetag_last_activity";
/** Item 5: the Dashboard card asked to open this JSP at the close pill once its QEW page loads. */
const CLOSE_REQUEST_KEY = "safetag_close_jsp";

/** The JSP the Dashboard card asked to close, read once. */
function takeCloseRequest(): string | null {
  try {
    const v = sessionStorage.getItem(CLOSE_REQUEST_KEY);
    sessionStorage.removeItem(CLOSE_REQUEST_KEY);
    return v;
  } catch {
    return null;
  }
}
/** How long a good bag tag read stays on screen before the name check opens. */
const SCAN_HOLD_MS = 1000;
let memoryActivity = Date.now();

function readActivity(): Date {
  try {
    const v = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    if (v > 0) return new Date(v);
  } catch {
    /* storage blocked: keep it in memory */
  }
  return new Date(memoryActivity);
}

function touchActivity(): void {
  memoryActivity = Date.now();
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(memoryActivity));
  } catch {
    /* storage blocked */
  }
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** The signed-in QEW, if the visit is still open. An idle visit is signed out here. */
export async function openQewVisit(): Promise<SignInClaims | null> {
  const b = await currentBadge("field").catch(() => null);
  if (!b || !isQewSignIn(b)) return null;
  if (isSessionExpired(readActivity(), new Date())) {
    await signOutNow("field").catch(() => undefined);
    return null;
  }
  return b;
}

const ADMIN_REQUEST_KEY = "safetag_want_admin";
const ADMIN_RETURN_KEY = "safetag_admin_return";

/**
 * Where Admin ID came from, so the admin sign-in can offer Cancel back to it (NAV_AUDIT.md item 8).
 * Same-site paths only. Kept for the visit, cleared once the admin is signed in.
 */
export function adminReturnPath(): string {
  try {
    const v = sessionStorage.getItem(ADMIN_RETURN_KEY) ?? "";
    return v.startsWith("/?") ? v : "";
  } catch {
    return "";
  }
}

export function clearAdminReturn(): void {
  try {
    sessionStorage.removeItem(ADMIN_RETURN_KEY);
  } catch {
    /* nothing kept */
  }
}

/** The QEW tapped through to the admin sign-in: the root page shows it, not his Dashboard. Read once. */
export function takeAdminRequest(): boolean {
  try {
    const v = sessionStorage.getItem(ADMIN_REQUEST_KEY) === "1";
    sessionStorage.removeItem(ADMIN_REQUEST_KEY);
    return v;
  } catch {
    return false;
  }
}

type View =
  | { kind: "gate"; note: string; scanning: boolean }
  | { kind: "typed"; digits: string }
  | { kind: "wait"; text: string }
  | { kind: "stop"; message: string; tagId: string; unclaimed: boolean }
  | { kind: "dashboard"; data: MyDashboard | null; error: string }
  | { kind: "loto"; data: MyDashboard; date: string; none: boolean; error: string; busy: boolean }
  | ({ kind: "gloves" } & GloveCardView)
  | ({ kind: "gloveArrive" } & GloveArrive)
  | { kind: "gloveRemove"; data: MyDashboard; reason: string; notes: string; error: string; busy: boolean }
  | { kind: "resources"; data: MyDashboard; lib: SopLibrary | null; error: string }
  | { kind: "sop"; data: MyDashboard; lib: SopLibrary; sop: SopEntry; frameUrl: string | null; error: string }
  | { kind: "work"; work: AssetWork | null; taskId: string; note: string; error: string; confirmDiscard?: boolean; confirmCancel?: boolean }
  | ({ kind: "sopRead"; taskId: string; ack: SopAck | null } & SopReadState)
  | { kind: "jsp"; work: AssetWork; taskId: string; jspNumber: string; draft: JspDraft | null; reopen?: { autoAt: string | null } };

export function mountFieldAccess(root: HTMLElement, company: string, start: FieldAccessStart): void {
  document.body.classList.remove("guest");
  document.body.classList.add("fa");
  root.innerHTML = '<div class="fa-wrap" id="fa"></div>';
  const host = root.querySelector<HTMLElement>("#fa")!;
  const fromAsset = start.kind === "asset" ? start : null;
  let siteTitle = fromAsset?.siteName ? `Alltech — ${fromAsset.siteName}` : "SafeTag";
  let scanner: QrScanner | null = null;
  let idleTimer: number | null = null;
  let view: View = { kind: "wait", text: "Loading…" };

  // ---- Back and refresh, like other apps (nav-history.ts, approved 2026-10-09) ----
  // Every page change goes through go(), which tells the browser: a new step, one step up, or a replace.
  // The phone's Back gesture and the browser Back button then run the same Back the page's own button runs.
  const baseUrl = location.pathname + location.search;
  const startPage = pageFromHash(location.hash);
  let stack: Page[] = startPage ? chainOf(startPage, Boolean(fromAsset)) : fromAsset ? ["guest"] : [];
  // A refresh rebuilds his page before history follows again.
  let restoring = Boolean(startPage);
  let pendingBack = 0;
  const afterBack: (() => void)[] = [];
  let jspOverlayOpen = false;
  let jspHandle: JspHandle | null = null;
  // Item 5: the Dashboard close card (JSP_CLOSE_NET_WORDS.md).
  let cardNoteOpen = false;
  let cardBusy = false;
  let cardError = "";
  let cardThanks = "";
  // EEWP rev 3 decision 4: Review And Agree on the second QEW's Dashboard.
  const askRead: Record<string, boolean> = {};
  let askBusy = "";
  let askError = "";
  let askThanks = "";

  function pageOf(v: View): Page | null {
    switch (v.kind) {
      case "wait":
        return null;
      case "gate":
      case "typed":
      case "stop":
        return "gate";
      case "work":
        return v.confirmDiscard || v.confirmCancel ? "discard" : "qew";
      case "jsp":
        return jspOverlayOpen ? "jspOverlay" : "jsp";
      default:
        return v.kind;
    }
  }

  function urlFor(p: Page): string {
    const h = hashOf(p);
    return h ? `${baseUrl}#${h}` : baseUrl;
  }

  function syncHistory(): void {
    if (restoring || pendingBack) return;
    const p = pageOf(view);
    if (!p) return;
    const r = moveTo(stack, p, Boolean(fromAsset));
    stack = r.stack;
    if (r.move === "push") history.pushState({ fa: stack.length }, "", urlFor(p));
    else if (r.move === "up") {
      pendingBack++;
      history.back();
    } else history.replaceState({ fa: stack.length }, "", urlFor(p));
  }

  /** Back to the guest page for this asset. He stays signed in. */
  function toGuestPage(): void {
    stopCamera();
    if (!fromAsset) return;
    if (stack.length === 2 && stack[0] === "guest" && !pendingBack) {
      stack = ["guest"];
      pendingBack++;
      afterBack.push(() => location.reload());
      history.back();
    } else location.assign(keepAdminBack(`/?aid=${encodeURIComponent(fromAsset.assetId)}`, location.search));
  }

  /** The Back gesture: the same as the page's own Back, Cancel or Close. */
  function backFrom(v: View): void {
    switch (v.kind) {
      case "jsp":
        if (jspOverlayOpen) jspHandle?.closeOverlay();
        else jspHandle?.back();
        return;
      case "work":
        if (v.confirmDiscard || v.confirmCancel) go({ ...v, confirmDiscard: false, confirmCancel: false });
        return;
      case "sopRead":
        backToWork(v.work, v.taskId, "");
        return;
      case "dashboard":
        void openWork();
        return;
      case "loto":
      case "gloves":
      case "resources":
        go({ kind: "dashboard", data: v.data, error: "" });
        return;
      case "gloveArrive":
      case "gloveRemove":
        go(gloveCard(v.data));
        return;
      case "sop":
        go({ kind: "resources", data: v.data, lib: v.lib, error: "" });
        return;
      default:
        return;
    }
  }

  /** A long press on Back can jump several pages. Open the page he landed on. */
  async function openPage(p: Page): Promise<void> {
    if (p === "qew" || p === "discard" || p === "sopRead" || p === "jsp" || p === "jspOverlay") return openWork();
    const d = await openDashboard();
    if (!d) return;
    if (p === "gloves" || p === "gloveArrive" || p === "gloveRemove") go(gloveCard(d));
    else if (p === "resources" || p === "sop") await openResources(d);
  }

  window.addEventListener("popstate", (ev) => {
    if (!host.isConnected) return;
    if (pendingBack) {
      pendingBack--;
      const next = afterBack.shift();
      if (next) next();
      else syncHistory();
      return;
    }
    const depth = typeof (ev.state as { fa?: unknown } | null)?.fa === "number" ? ((ev.state as { fa: number }).fa) : 1;
    // Forward is not a page here. Step back to where he was.
    if (depth >= stack.length) {
      pendingBack++;
      history.back();
      return;
    }
    const from = view;
    const fromPage = pageOf(from);
    stack = stack.slice(0, Math.max(depth, 1));
    const to = stack[stack.length - 1];
    // Signed out: Back never walks into his pages.
    if (fromPage === "gate") {
      if (fromAsset) return location.replace(`/?aid=${encodeURIComponent(fromAsset.assetId)}`);
      return syncHistory();
    }
    if (!to || to === "guest") {
      if (fromAsset) location.reload();
      return;
    }
    if (fromPage && parentOf(fromPage, Boolean(fromAsset)) === to) backFrom(from);
    else void openPage(to);
  });
  // My Tasks tiers the QEW has opened. All closed when the Dashboard opens.
  const openTiers = new Set<number>();
  // SOP Library tiers the QEW has opened. All closed when Resources opens.
  const openSopTiers = new Set<number>();
  // The review copy of the SOP showing now. Released when it closes.
  let sopFrameUrl: string | null = null;
  // Where the gate's Cancel goes back to: the "Is this you?" that sent him here.
  // Practice reads finished on a demo asset this visit. They open Start for the demo JSP only.
  const practiced = new Set<string>();

  // ---- Screens ----

  function top(button: string): string {
    return `<div class="fa-top"><div><div class="nm">${esc(siteTitle)}</div><div class="pg">Electrical Safety Program</div></div>${button}</div>`;
  }
  const cancelBtn = '<button class="pbtn" type="button" data-fa="cancel">Cancel</button>';

  function gateHtml(note: string, scanning: boolean): string {
    const back = fromAsset ? cancelBtn : "";
    const choose = `<button class="fa-scanbtn" type="button" data-fa="scan">Scan Bag Tag QR<small>Opens the camera. Point it at the QR on your yellow bag tag.</small></button>
      <div class="fa-or">OR</div>
      <button class="fa-typein" type="button" data-fa="type">00-QEW__<span>Type QEW ID</span></button>`;
    const camera = `<div class="fa-card"><h3>Field Access</h3>
        <div class="fa-cam"><video id="fa-video" playsinline muted></video><div class="fr"></div><div class="tx" id="fa-cam-tx">Starting the camera…</div></div>
        <div class="fa-hint" id="fa-scan-hint">Scan the QR on your yellow bag tag.</div></div>
      <button class="fa-plain" type="button" data-fa="scan-stop">Cancel</button>`;
    return `${top(back)}<div class="fa-body">
      ${note ? `<div class="fa-note">${esc(note)}</div>` : ""}
      ${scanning ? camera : choose}
      ${fromAsset ? `<div class="fa-hint" style="text-align:left;padding:0 4px">Working on ${esc(fromAsset.assetId)}.</div>` : ""}
      ${scanning ? "" : '<button class="fa-link" type="button" data-fa="admin">Admin ID</button>'}
    </div>`;
  }

  function typedHtml(digits: string): string {
    const site = digits.slice(0, 2).padEnd(2, "_");
    const num = digits.slice(2, 4).padEnd(2, "_");
    const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => `<button type="button" data-fa-key="${k}">${k}</button>`).join("");
    return `${top('<button class="pbtn" type="button" data-fa="gate">Cancel</button>')}<div class="fa-body">
      <div class="fa-card"><h3>QEW ID</h3>
        <div class="fa-idbox"><span class="b">${site}</span><span class="fx">-QEW</span><span class="b">${num}</span></div>
        <div class="fa-hint" style="border-top:1px solid #d9dce1">Site number, then your number. QEW is filled in.</div></div>
      <div style="flex:1"></div>
      <div class="fa-keys">${keys}<button type="button" class="blank" tabindex="-1"></button><button type="button" data-fa-key="0">0</button><button type="button" class="fn" data-fa="del">Delete</button></div>
      <button class="fa-go" type="button" data-fa="typed-next"${typedQewId(digits) ? "" : " disabled"}>Next</button>
    </div>`;
  }

  function stopHtml(message: string, tagId: string, unclaimed: boolean): string {
    const claim = unclaimed ? `<a class="fa-plain" href="${keepAdminBack(`/?tag=${encodeURIComponent(tagId)}`, location.search)}">Open This Tag</a>` : "";
    return `${top(cancelBtn)}<div class="fa-body">
      <div class="fa-note red">${esc(message)}</div>
      <button class="fa-go" type="button" data-fa="gate">Try Again</button>${claim}
    </div>`;
  }

  /** How did you leave it? (JSP_CLOSE_NET_WORDS.md section 2). One card at a time. */
  function closeCardHtml(c: CloseCard | null): string {
    if (!c) return cardThanks ? `<div class="fa-close done">${esc(cardThanks)}</div>` : "";
    const n = c.jspNumber.replace("JSP-", "");
    let h = `<div class="fa-close"><div class="t">How did you leave it?</div><div class="m">${esc(`${c.assetId} · ${c.taskName} · JSP #${n} · ${c.submittedDay}`)}</div>`;
    if (c.autoAt) h += `<div class="auto">${esc(`Closed by the app at ${c.autoAt}. Tell us how you left it.`)}</div>`;
    if (c.isDeEnergized) {
      // Isolation and the re-energize checks are answered on the close pill (Robert, 2026-10-09).
      h += `<button class="fa-close-open" type="button" data-fa="card-open">${c.autoAt ? `Answer JSP #${esc(n)}` : `Close JSP #${esc(n)}`}</button>`;
    } else if (cardNoteOpen) {
      h += `<label class="q" for="fa-card-note">${esc(REPAIR_NOTE_PROMPT)}</label><textarea id="fa-card-note" class="fa-close-note" placeholder="Optional"></textarea>
        <div class="row"><button class="fa-plain" type="button" data-fa="card-cancel">Cancel</button><button class="fa-go" type="button" data-fa="card-send"${cardBusy ? " disabled" : ""}>${cardBusy ? "Sending…" : "Send"}</button></div>`;
    } else {
      h += `<div class="olist"><button class="fa-close-o" type="button" data-fa="card-left" data-left="same"${cardBusy ? " disabled" : ""}>${esc(LEFT_AS_LABEL.same)}</button>`;
      if (c.hasFindings) h += `<button class="fa-close-o" type="button" data-fa="card-left" data-left="fixed"${cardBusy ? " disabled" : ""}>${esc(LEFT_AS_LABEL.fixed)}</button>`;
      h += `<button class="fa-close-o" type="button" data-fa="card-worse"${cardBusy ? " disabled" : ""}>${esc(LEFT_AS_LABEL.worse)}</button></div>`;
    }
    if (cardError) h += `<div class="fa-note red">${esc(cardError)}</div>`;
    return `${h}</div>`;
  }

  /** One amber card per permit that names this QEW as second QEW. Agreement, not approval. */
  function askCardsHtml(asks: EewpAsk[]): string {
    if (!asks.length) return askThanks ? `<div class="fa-close done">${esc(askThanks)}</div>` : "";
    return asks
      .map((a) => {
        const read = Boolean(askRead[a.permitId]);
        const busy = askBusy === a.permitId;
        return `<div class="fa-ask"><div class="e">Review And Agree · ${esc(a.permitId)}</div>
          <div class="t">${esc(a.requesterName)} named you as second QEW to work on ${esc(a.equipment || a.assetId)} with the power on.</div>
          <div class="r"><span>The work</span><b>${esc(a.liveWork)}</b></div>
          <div class="r"><span>Your role</span><b>Stand by outside the arc flash boundary. ${esc(a.emergencyPlan)}</b></div>
          <div class="r"><span>Expires</span><b>${esc(clockOf(a.expiresAt))}</b></div>
          <button class="fa-ask-read${read ? " on" : ""}" type="button" data-fa="ask-read" data-permit="${esc(a.permitId)}"><span class="bx">${read ? "✓" : ""}</span><span><b>I read the plan.</b><small>Your name is recorded on ${esc(a.permitId)}.</small></span></button>
          <div class="q">Can this work be done safely?</div>
          <div class="row"><button class="fa-plain" type="button" data-fa="ask-no" data-permit="${esc(a.permitId)}"${busy ? " disabled" : ""}>No</button><button class="fa-go" type="button" data-fa="ask-yes" data-permit="${esc(a.permitId)}"${busy || !read ? " disabled" : ""}>${busy ? "Sending…" : "I Agree"}</button></div>
          <div class="m">No ends the permit. This is your agreement as a QEW. It is not an approval.</div>
          ${askError && busy === false && askError.startsWith(a.permitId) ? `<div class="fa-note red">${esc(askError.slice(a.permitId.length + 1))}</div>` : ""}</div>`;
      })
      .join("");
  }

  async function answerAsk(permitId: string, agree: boolean): Promise<void> {
    if (view.kind !== "dashboard" || !view.data || askBusy) return;
    const d = view.data;
    askBusy = permitId;
    askError = "";
    draw();
    try {
      await callServer("agreeEewp", { permitId, agree, readPlan: Boolean(askRead[permitId]) });
      askThanks = agree ? `Thanks. You agreed on ${permitId}.` : `${permitId} ends. The QEW and approvers are told.`;
      if (view.kind === "dashboard" && view.data === d) go({ kind: "dashboard", data: { ...d, eewpAsks: (d.eewpAsks ?? []).filter((a) => a.permitId !== permitId) }, error: "" });
    } catch (e) {
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      askError = `${permitId} It did not save. ${messageOf(e)}`;
    } finally {
      askBusy = "";
      draw();
    }
  }

  function dashboardHtml(d: MyDashboard | null, error: string): string {
    // The way back sits top right, like every other page (approved 2026-10-09). No arrow.
    const back = fromAsset ? '<button class="fa-idb-bk" type="button" data-fa="work">QEW Page</button>' : "";
    if (!d) {
      return `${back ? `<div class="fa-navh"><div><div class="e">SAFETAG · MY DASHBOARD</div></div>${back}</div>` : ""}<div class="fa-body">${error ? `<div class="fa-note red">${esc(error)}</div><button class="fa-go" type="button" data-fa="reload-dash">Try Again</button>` : '<div class="fa-wait">Loading your Dashboard…</div>'}
        <button class="fa-so" type="button" data-fa="signout">Sign Out</button></div>`;
    }
    const today = dateInSiteTime(new Date());
    const card = qewTrainingCard(
      d.records.map((r) => ({ nodeId: r.nodeId as NodeId, completedOn: r.completedOn, expiresOn: r.expiresOn })),
      today,
    );
    const st = dashboardStatus(card.trainingComplete, d);
    const qewActive = st.tone === "green";
    const loto = lotoCardOf(d, qewActive, today);
    return `<div class="fa-idb ${st.tone}${back ? " hasbk" : ""}">${back}<div class="e">SAFETAG · MY DASHBOARD</div><div class="nm">${esc(d.holderName)}</div>
        <div class="tg">${esc(d.badgeId)}${d.siteName ? ` · ${esc(d.siteName)}` : ""}</div><div class="st">${esc(st.text)}</div></div>
      <div class="fa-body">
        ${askCardsHtml(d.eewpAsks ?? [])}
        ${closeCardHtml(d.closeCard ?? null)}
        ${qewTrainingCardHtml(card)}
        <button class="fa-loto" type="button" data-fa="loto"><span class="h">LOTO <span class="chev">›</span></span>
          <span class="b"><span class="s ${loto.tone}">${esc(loto.title)}</span><span class="m">${esc(loto.line)}</span></span></button>
        ${loto.askToReport ? '<button class="fa-gold" type="button" data-fa="loto">Report LOTO Date</button>' : ""}
        ${myTasksCardHtml(myTasksCard(d.tasks ?? [], d.taskAuthorizations ?? [], d.taskAuthCycleDays, today), openTiers)}
        ${gloveRowHtml(d, card.glovesOpen, today)}
        <button class="fa-ppe" type="button" data-fa="resources"><span><b>Resources</b><small>SOP library · inspection programs</small></span><span class="chev">›</span></button>
        <button class="fa-so" type="button" data-fa="signout">Sign Out</button>
      </div>`;
  }

  // ---- Resources ----

  function navHead(eyebrow: string, title: string, button: string): string {
    return `<div class="fa-navh"><div><div class="e">${esc(eyebrow)}</div><div class="t">${esc(title)}</div></div>${button}</div>`;
  }

  function siteWord(d: MyDashboard): string {
    return (d.siteName.split(",")[0] ?? "").trim().toUpperCase();
  }

  function sopRowHtml(s: SopEntry): string {
    return `<button class="res-row" type="button" data-fa="sop" data-sop="${esc(s.sopNumber)}"><span class="res-chip">${esc(s.chip)}</span>
      <span class="t"><b>${esc(s.title)}</b><small>${esc(s.tasks.join(" · "))}</small></span><span class="chev">›</span></button>`;
  }

  function libraryHtml(lib: SopLibrary): string {
    if (lib.total === 0) return '<div class="fa-note grey">The SOP Library is not loaded yet. See your ESP Manager.</div>';
    const permit = lib.permit
      ? `<button class="mt-fold res-permit" type="button" data-fa="sop" data-sop="${esc(lib.permit.sopNumber)}"><span class="chip gold"></span>
          <span class="t"><b>ESWC Procedure</b><small>${esc(lib.permit.sopNumber)} · Energized and Tier 3 work permit</small></span><span class="ar">›</span></button>`
      : "";
    const tiers = lib.tiers
      .filter((t) => t.sops.length > 0)
      .map((t) => {
        const isOpen = openSopTiers.has(t.tier);
        const head = `<button class="mt-fold" type="button" data-fa="sop-tier" data-tier="${t.tier}" aria-expanded="${isOpen}"><span class="chip"></span>
          <span class="t"><b>${t.title}</b><small>${esc(t.sub)}</small></span><span class="f">${t.sops.length} SOP${t.sops.length === 1 ? "" : "s"}</span><span class="ar">${isOpen ? "▾" : "›"}</span></button>`;
        return isOpen ? head + t.sops.map(sopRowHtml).join("") : head;
      })
      .join("");
    return `<section class="mt res"><h3>SOP Library <span>${lib.total} <small>SOPs</small></span></h3>${permit}${tiers}
      <div class="mt-foot">${esc(LIBRARY_FOOT)}</div></section>`;
  }

  function resourcesHtml(v: Extract<View, { kind: "resources" }>): string {
    const d = v.data;
    const head = navHead(`${d.badgeId} · ${siteWord(d)}`, "Resources", '<button class="bk" type="button" data-fa="res-back">Back</button>');
    const lib = v.lib
      ? libraryHtml(v.lib)
      : v.error
        ? `<div class="fa-note red">${esc(v.error)}</div><button class="fa-go" type="button" data-fa="resources">Try Again</button>`
        : '<div class="fa-wait">Loading the SOP Library…</div>';
    const programs = INSPECTION_PROGRAMS.map(
      (p) => `<a class="fa-ppe" href="${esc(p.url)}" target="_blank" rel="noopener"><span><b>${esc(p.name)}</b><small>${esc(p.line)}</small></span><span class="chev">›</span></a>`,
    ).join("");
    return `${head}<div class="fa-body">${lib}<span class="fa-lbl">Inspection Programs</span>${programs}</div>`;
  }

  function sopHtml(v: Extract<View, { kind: "sop" }>): string {
    const head = navHead(`${v.sop.sopNumber} · RESOURCES`, v.sop.title, '<button class="bk" type="button" data-fa="sop-close">Close</button>');
    const body = v.error
      ? `<div class="fa-body"><div class="fa-note red">${esc(v.error)}</div><button class="fa-go" type="button" data-fa="sop-retry">Try Again</button></div>`
      : v.frameUrl
        ? `<div class="res-review">${esc(REVIEW_LINE)}</div><iframe class="res-frame" title="${esc(v.sop.sopNumber)}" src="${esc(v.frameUrl)}"></iframe>`
        : `<div class="fa-body"><div class="fa-wait">Opening ${esc(v.sop.sopNumber)}…</div></div>`;
    return head + body;
  }

  // ---- Gloves ----

  function gloveViewOf(d: MyDashboard): GloveView {
    return { gloveSize: d.gloveSize ?? null, pair: d.gloves ?? null, lastInspection: d.lastGloveInspection ?? null, lastRetired: d.lastRetiredGloves ?? null };
  }

  function gloveRowHtml(d: MyDashboard, open: boolean, today: string): string {
    if (!open) return '<div class="fa-lock"><b>PPE and gloves</b>Opens once Nodes 1 to 3 are done.</div>';
    const g = gloveCardOf(gloveViewOf(d), today);
    return `<button class="fa-ppe" type="button" data-fa="gloves"><span><b>PPE and gloves</b><small class="${g.dashTone}">${esc(g.dashLine)}</small></span><span class="chev">›</span></button>`;
  }

  function gloveHead(d: MyDashboard, what: string, button: string): string {
    const site = (d.siteName.split(",")[0] ?? "").trim().toUpperCase();
    return `<div class="fa-navh"><div><div class="e">${esc(d.badgeId)} · ${esc(what || site)}</div><div class="t">${esc(d.holderName)}</div></div>${button}</div>`;
  }

  function sizeGrid(picked: string): string {
    return `<span class="fa-lbl">Size</span><div class="gl-sizes">${GLOVE_SIZES.map((z) => `<button type="button" data-fa="glove-size" data-size="${z}" class="${z === picked ? "on" : ""}">${z}</button>`).join("")}</div>
      <span class="fa-lbl">Class</span><div class="gl-lock">${esc(GLOVE_CLASS_LINE)}</div>`;
  }

  function glovesHtml(v: GloveCardView): string {
    const d = v.data;
    const g = gloveCardOf(gloveViewOf(d), dateInSiteTime(new Date()));
    const err = v.error ? `<div class="fa-note red">${esc(v.error)}</div>` : "";
    const head = gloveHead(d, "", `<button class="bk" type="button" data-fa="glove-back"${v.busy ? " disabled" : ""}>Back</button>`);
    if (g.state === "pickSize" || v.editingSize) {
      return `${head}<div class="fa-body">
        <div class="gl-state wait"><small>Rubber insulating gloves</small><span>${v.editingSize ? "Change Your Size" : "Pick Your Size"}</span></div>
        <div class="gl-note plain">${v.editingSize ? "Pick the size for your next pair." : "Training is done. Pick your glove size so your gloves can be ordered."}</div>
        ${sizeGrid(v.pickSize)}${err}
        <div style="flex:1"></div>
        <button class="fa-go" type="button" data-fa="glove-size-save"${v.pickSize && !v.busy && v.pickSize !== (v.editingSize ? d.gloveSize : "") ? "" : " disabled"}>${v.busy ? "Saving…" : "Save Size"}</button>
        ${v.editingSize ? '<button class="fa-link" type="button" data-fa="glove-size-cancel">Cancel</button>' : ""}
      </div>`;
    }
    const rows = g.rows
      .map((r) => `<div class="kv"><span>${esc(r.label)}</span><b class="${r.tone ?? ""}${r.mono ? " mono" : ""}">${esc(r.value)}</b></div>`)
      .join("");
    const buttons = g.actions
      .map((a) =>
        a === "placeInService"
          ? `<button class="fa-green" type="button" data-fa="glove-place"${v.busy ? " disabled" : ""}>${v.busy ? "Saving…" : "Place In Service"}</button>`
          : a === "arrived"
            ? `<button class="fa-gold" type="button" data-fa="glove-arrived"${v.busy ? " disabled" : ""}>Gloves Arrived</button>`
            : "",
      )
      .join("");
    return `${head}<div class="fa-body">
        <div class="gl-state ${g.tone}"><small>Rubber insulating gloves</small><span>${esc(g.title)}</span></div>
        ${g.retiredLine ? `<div class="gl-note red">${esc(g.retiredLine)}</div>` : ""}
        ${rows ? `<div class="gl-card">${rows}</div>` : ""}
        ${g.note ? `<div class="gl-note ${g.note.tone}">${esc(g.note.text)}</div>` : ""}
        ${err}
        <div style="flex:1"></div>
        ${buttons}
        ${g.showChangeSize ? '<button class="fa-link" type="button" data-fa="glove-size-change">Change Size</button>' : ""}
        ${g.showRemove ? `<button class="fa-link" type="button" data-fa="glove-remove"${v.busy ? " disabled" : ""}>Remove From Service</button>` : ""}
      </div>`;
  }

  function arrivalOf(v: GloveArrive) {
    return { sealed: v.sealed, testedOn: v.testedOn, firstUse: v.firstUseMode === "today" ? "today" : v.firstUseMode === "earlier" ? v.firstUse : "" };
  }

  function gloveArriveHtml(v: GloveArrive): string {
    const today = dateInSiteTime(new Date());
    const problem = arrivalProblem(arrivalOf(v), today);
    const first = v.sealed === null;
    const head = gloveHead(
      v.data,
      "GLOVES ARRIVED",
      `<button class="bk" type="button" data-fa="${first ? "glove-cancel" : "glove-step-back"}"${v.busy ? " disabled" : ""}>${first ? "Cancel" : "Back"}</button>`,
    );
    const replacing = v.replacing
      ? `<div class="gl-note plain">Saving the new pair retires <b class="mono">${esc(v.replacing)}</b> as Replaced. Discard it once the new pair goes in service.</div>`
      : "";
    if (first) {
      return `${head}<div class="fa-body">${replacing}
        <span class="gl-step">Step 1</span>
        <div class="gl-ask">Are the gloves still sealed in the manufacturer's bag?</div>
        <button class="fa-go" type="button" data-fa="glove-sealed" data-v="yes">Yes, Sealed</button>
        <button class="fa-go" type="button" data-fa="glove-sealed" data-v="no">No, Opened</button>
        <div class="gl-note plain">Size ${esc(v.data.gloveSize ?? "")}, Class 0. Change size on the card if wrong.</div>
      </div>`;
    }
    const parts = arrivalParts(v, today);
    return `${head}<div class="fa-body">${replacing}
        <div class="gl-done">${v.sealed ? "Sealed in the bag ✓" : "Opened ✓"}</div>
        <span class="gl-step">Step 2</span>
        <label class="fa-lbl" for="gl-tested">${v.sealed ? "Test date on the bag" : "Test date on the gloves"}</label>
        <input class="fa-in${parts.testBad ? " bad" : ""}" id="gl-tested" type="date" max="${today}" value="${esc(v.testedOn)}">
        <div id="gl-test-err">${parts.testErr}</div>
        ${v.sealed ? '<div class="gl-note plain">Keep the bag sealed until you need them. The 12-month clock runs from the test date.</div>' : ""}
        <div id="gl-step3">${parts.step3}</div>
        ${v.error ? `<div class="fa-note red">${esc(v.error)}</div>` : ""}
        <div style="flex:1"></div>
        <button class="fa-go" type="button" id="gl-arrive-save" data-fa="glove-arrive-save"${parts.canSave && !v.busy ? "" : " disabled"}>${v.busy ? "Saving…" : "Save Gloves"}</button>
      </div>`;
  }

  /** The parts of Gloves Arrived that follow the answers. Built apart so a date picker is never redrawn. */
  function arrivalParts(v: GloveArrive, today: string) {
    const problem = arrivalProblem(arrivalOf(v), today);
    const err = (field: string, hasValue: boolean) =>
      problem && problem.field === field && hasValue ? `<div class="gl-err">${esc(problem.message)}</div>` : "";
    const testOk = !testDateProblem(v.testedOn, today);
    const firstErr = err("firstUse", v.firstUseMode === "today" || Boolean(v.firstUse));
    const step3 =
      !v.sealed && testOk
        ? `<span class="gl-step">Step 3</span>
        <div class="gl-ask sm">When did you first use them?</div>
        <div class="gl-chips two"><button type="button" data-fa="glove-first" data-v="today" class="${v.firstUseMode === "today" ? "on" : ""}">Today</button><button type="button" data-fa="glove-first" data-v="earlier" class="${v.firstUseMode === "earlier" ? "on" : ""}">Earlier</button></div>
        ${v.firstUseMode === "earlier" ? `<input class="fa-in${problem?.field === "firstUse" && v.firstUse ? " bad" : ""}" id="gl-firstuse" type="date" max="${today}" value="${esc(v.firstUse)}">` : ""}
        <div id="gl-first-err">${firstErr}</div>`
        : "";
    return {
      testBad: problem?.field === "testedOn" && Boolean(v.testedOn),
      testErr: err("testedOn", Boolean(v.testedOn)),
      testOk,
      step3,
      firstBad: problem?.field === "firstUse" && Boolean(v.firstUse),
      firstErr,
      canSave: !problem,
    };
  }

  /**
   * A date was picked on Gloves Arrived. Only the message, step 3 and Save change.
   * The date box itself is left alone, so the picker stays open to read (Robert, 2026-10-06).
   */
  function refreshArrival(v: GloveArrive): void {
    const parts = arrivalParts(v, dateInSiteTime(new Date()));
    host.querySelector("#gl-tested")?.classList.toggle("bad", parts.testBad);
    const testErr = host.querySelector("#gl-test-err");
    if (testErr) testErr.innerHTML = parts.testErr;
    const step3 = host.querySelector<HTMLElement>("#gl-step3");
    const shown = Boolean(step3?.firstElementChild);
    if (step3 && shown !== Boolean(parts.step3)) step3.innerHTML = parts.step3;
    host.querySelector("#gl-firstuse")?.classList.toggle("bad", parts.firstBad);
    const firstErr = host.querySelector("#gl-first-err");
    if (firstErr) firstErr.innerHTML = parts.firstErr;
    const save = host.querySelector<HTMLButtonElement>("#gl-arrive-save");
    if (save) save.disabled = !parts.canSave || v.busy;
  }

  function gloveRemoveHtml(v: Extract<View, { kind: "gloveRemove" }>): string {
    const chips = REMOVAL_REASONS.map((r) => `<button type="button" data-fa="glove-reason" data-reason="${r}" class="${r === v.reason ? "on" : ""}">${r}</button>`).join("");
    return `${gloveHead(v.data, "REMOVE FROM SERVICE", `<button class="bk" type="button" data-fa="glove-cancel"${v.busy ? " disabled" : ""}>Cancel</button>`)}
      <div class="fa-body">
        <div class="gl-note plain">For a damaged or lost pair. The pair stays on record. You go back to Waiting On Gloves with your size kept.</div>
        <span class="fa-lbl">Reason</span>
        <div class="gl-chips two">${chips}</div>
        <label class="fa-lbl" for="gl-notes">Notes (optional)</label>
        <input class="fa-in" id="gl-notes" type="text" maxlength="300" placeholder="Anything worth noting" value="${esc(v.notes)}">
        ${v.error ? `<div class="fa-note red">${esc(v.error)}</div>` : ""}
        <div style="flex:1"></div>
        <button class="fa-red" type="button" data-fa="glove-remove-go"${v.reason && !v.busy ? "" : " disabled"}>${v.busy ? "Saving…" : "Confirm Removal"}</button>
      </div>`;
  }

  function lotoHtml(v: Extract<View, { kind: "loto" }>): string {
    const today = dateInSiteTime(new Date());
    return `<div class="fa-navh"><div><div class="e">MY DASHBOARD · LOTO</div><div class="t">${esc(v.data.holderName)}</div></div>
        <button class="bk" type="button" data-fa="loto-cancel"${v.busy ? " disabled" : ""}>Cancel</button></div>
      <div class="fa-body">
        <label class="fa-lbl" for="fa-loto-date">Your LOTO authorization date</label>
        <input class="fa-in" id="fa-loto-date" type="date" max="${today}" value="${esc(v.date)}"${v.none ? " disabled" : ""}>
        <label class="fa-chk"><input type="checkbox" id="fa-loto-none"${v.none ? " checked" : ""}><span>I don't currently have a LOTO authorization</span></label>
        <div class="fa-note grey">Your department manager, site manager or Safety gets an email to confirm this date. You are a QEW once it is confirmed.</div>
        ${v.error ? `<div class="fa-note red">${esc(v.error)}</div>` : ""}
        <div style="flex:1"></div>
        <button class="fa-go" type="button" data-fa="loto-send"${v.busy ? " disabled" : ""}>${v.busy ? "Sending…" : "Send for Verification"}</button>
      </div>`;
  }

  function draw(): void {
    if (view.kind !== "gate" || !view.scanning) stopCamera();
    switch (view.kind) {
      case "gate":
        host.innerHTML = gateHtml(view.note, view.scanning);
        if (view.scanning) startCamera();
        return;
      case "typed":
        host.innerHTML = typedHtml(view.digits);
        return;
      case "wait":
        // Cancel on the wait (NAV_CRAWL_WORDS.md A8): weak signal can hold this a minute. Back where he came from.
        // Top right, where the gate keeps its Cancel.
        host.innerHTML = `${top('<button class="pbtn" type="button" data-fa="wait-cancel">Cancel</button>')}<div class="fa-body"><div class="fa-wait">${esc(view.text)}</div></div>`;
        return;
      case "stop":
        host.innerHTML = stopHtml(view.message, view.tagId, view.unclaimed);
        return;
      case "loto":
        host.innerHTML = lotoHtml(view);
        return;
      case "gloves":
        host.innerHTML = glovesHtml(view);
        return;
      case "gloveArrive":
        host.innerHTML = gloveArriveHtml(view);
        return;
      case "gloveRemove":
        host.innerHTML = gloveRemoveHtml(view);
        return;
      case "resources":
        host.innerHTML = resourcesHtml(view);
        return;
      case "sop":
        host.innerHTML = sopHtml(view);
        return;
      case "work":
        host.innerHTML = view.work
          ? workHtml({ work: view.work, taskId: view.taskId, practiced, note: view.note, confirmDiscard: view.confirmDiscard === true, confirmCancel: view.confirmCancel === true })
          : workLoadingHtml(fromAsset?.assetId ?? "", view.error);
        return;
      case "sopRead":
        host.innerHTML = sopReadHtml(view);
        return;
      case "jsp": {
        // The JSP owns its box and events. It hands back through onExit.
        host.innerHTML = '<div id="jsp-box"></div>';
        const v = view;
        jspHandle = mountJsp(host.querySelector<HTMLElement>("#jsp-box")!, {
          work: v.work,
          taskId: v.taskId,
          jspNumber: v.jspNumber,
          draft: v.draft,
          reopen: v.reopen,
          call: <T,>(name: string, data: Record<string, unknown>) => callServer<T>(name, { ...data, forBadgeId: v.work.qew.badgeId, company }),
          onExit: (note) => {
            if (view === v) void openWork(v.taskId, note);
          },
          onActivity: touchActivity,
          onOverlay: (open) => {
            if (view !== v) return;
            jspOverlayOpen = open;
            syncHistory();
          },
        });
        return;
      }
      default:
        host.innerHTML = dashboardHtml(view.data, view.error);
    }
  }

  function go(v: View): void {
    if (sopFrameUrl && ((v.kind !== "sop" && v.kind !== "sopRead") || v.frameUrl !== sopFrameUrl)) {
      URL.revokeObjectURL(sopFrameUrl);
      sopFrameUrl = null;
    }
    if (v.kind !== "jsp") {
      jspOverlayOpen = false;
      jspHandle = null;
    }
    view = v;
    draw();
    syncHistory();
  }

  // ---- Steps ----

  function startCamera(): void {
    let found: string | null = null;
    const video = host.querySelector<HTMLVideoElement>("#fa-video");
    const tx = host.querySelector<HTMLElement>("#fa-cam-tx");
    if (!video || !tx) return;
    scanner = startQrScanner(
      video,
      (link) => {
        const read = readQrLink(link);
        if (read.kind === "badge" && isAdminBadgeId(read.badgeId)) {
          toAdmin();
          return true;
        }
        if (read.kind === "badge" && /^\d{2}-QEW\d{2}$/.test(read.badgeId)) {
          // Hold a moment so the QEW sees what was read (Robert, 2026-10-06).
          if (found) return false;
          found = read.badgeId;
          host.querySelector(".fa-cam")?.classList.add("found");
          tx.textContent = `Found ${read.badgeId} ✓`;
          const hint = host.querySelector("#fa-scan-hint");
          if (hint) hint.textContent = "Bag tag read. Opening the name check.";
          window.setTimeout(() => {
            if (view.kind === "gate" && view.scanning && found === read.badgeId) void checkTag(read.badgeId);
          }, SCAN_HOLD_MS);
          return false;
        }
        const hint = host.querySelector("#fa-scan-hint");
        if (hint) hint.textContent = read.kind === "asset" ? "That is an asset label. Scan your yellow bag tag." : "That is not a SafeTag bag tag. Scan your yellow bag tag.";
        return false;
      },
      (text) => {
        tx.textContent = text.startsWith("Looking") || text.startsWith("Starting") ? text : `${text} Type your QEW ID instead.`;
      },
    );
  }

  /** Each wait gets a number. Cancel moves it on, so an answer that arrives later is dropped. */
  let waitTicket = 0;

  function stopCamera(): void {
    scanner?.stop();
    scanner = null;
  }

  async function checkTag(tagId: string): Promise<void> {
    const ticket = ++waitTicket;
    go({ kind: "wait", text: `Checking ${tagId}…` });
    try {
      const v = await callServer<TagView>("loadTag", { tagId, company });
      if (ticket !== waitTicket) return;
      const check = nameCheckOf(v);
      // "Is this you?" is gone (Robert, 2026-10-09, SIGNIN_PASS_WORDS.md). A good tag signs in at once.
      if (check.kind === "ask") {
        if (!fromAsset && check.siteName) siteTitle = `Alltech — ${check.siteName}`;
        await confirmYes(check.tagId, Boolean(fromAsset));
      } else go({ kind: "stop", message: check.message, tagId, unclaimed: check.kind === "unclaimed" });
    } catch (e) {
      if (ticket !== waitTicket) return;
      go({ kind: "stop", message: `Could not check the tag. ${messageOf(e)}`, tagId, unclaimed: false });
    }
  }

  async function confirmYes(tagId: string, toWork = false): Promise<void> {
    const ticket = ++waitTicket;
    go({ kind: "wait", text: "Signing in…" });
    try {
      const { token } = await callServer<{ token: string }>("qewSignIn", { tagId, company });
      // Cancel was tapped while it waited: do not sign in.
      if (ticket !== waitTicket) return;
      await signInWithBadgeToken(token);
      touchActivity();
      if (toWork) await openWork();
      else await openDashboard();
    } catch (e) {
      if (ticket !== waitTicket) return;
      go({ kind: "stop", message: messageOf(e), tagId, unclaimed: false });
    }
  }

  async function openDashboard(): Promise<MyDashboard | null> {
    openTiers.clear();
    cardThanks = "";
    cardNoteOpen = false;
    cardError = "";
    go({ kind: "dashboard", data: null, error: "" });
    watchIdle();
    try {
      const data = await callServer<MyDashboard>("loadMyDashboard", {});
      // He may have tapped away while it loaded (Back to the asset). Draw only if he is still here.
      if (view.kind !== "dashboard" || view.data) return null;
      if (!fromAsset && data.siteName) siteTitle = `Alltech — ${data.siteName}`;
      document.title = `${data.holderName} · SafeTag`;
      go({ kind: "dashboard", data, error: "" });
      return data;
    } catch (e) {
      if (view.kind === "dashboard" && !view.data) go({ kind: "dashboard", data: null, error: messageOf(e) });
      return null;
    }
  }

  function lotoInputs(): { date: string; none: boolean } {
    const date = host.querySelector<HTMLInputElement>("#fa-loto-date")?.value ?? "";
    const none = host.querySelector<HTMLInputElement>("#fa-loto-none")?.checked ?? false;
    return { date: none ? "" : date, none };
  }

  async function sendLoto(): Promise<void> {
    if (view.kind !== "loto" || view.busy) return;
    const { date, none } = lotoInputs();
    const data = view.data;
    const problem = lotoReportProblem(date, none, dateInSiteTime(new Date()));
    if (problem) return go({ kind: "loto", data, date, none, error: problem, busy: false });
    go({ kind: "loto", data, date, none, error: "", busy: true });
    try {
      await callServer<{ ok: true }>("reportLotoDate", { lotoDate: date, noneReported: none, forBadgeId: data.badgeId });
      touchActivity();
      await openDashboard();
    } catch (e) {
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      go({ kind: "loto", data, date, none, error: `Not sent. ${messageOf(e)}`, busy: false });
    }
  }

  function gloveCard(data: MyDashboard, error = ""): View {
    return { kind: "gloves", data, pickSize: data.gloveSize ?? "", editingSize: false, busy: false, error };
  }

  /** Reloads the Dashboard data, then shows the glove card. */
  async function backToGloves(): Promise<void> {
    const data = await callServer<MyDashboard>("loadMyDashboard", {});
    go(gloveCard(data));
  }

  /** Runs one glove server step from the card, then reloads the card. */
  async function gloveStep(name: string, payload: Record<string, unknown>): Promise<void> {
    if (view.kind !== "gloves" || view.busy) return;
    const v = { ...view, error: "" };
    go({ ...v, busy: true });
    try {
      await callServer<unknown>(name, { ...payload, forBadgeId: v.data.badgeId });
      touchActivity();
      await backToGloves();
    } catch (e) {
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      go({ ...v, busy: false, error: `Not saved. ${messageOf(e)}` });
    }
  }

  async function saveArrival(): Promise<void> {
    if (view.kind !== "gloveArrive" || view.busy) return;
    const v = { ...view, error: "" };
    const input = arrivalOf(v);
    if (arrivalProblem(input, dateInSiteTime(new Date()))) return;
    go({ ...v, busy: true });
    try {
      await callServer<{ pairId: string }>("gloveArrived", { ...input, forBadgeId: v.data.badgeId });
      touchActivity();
      await backToGloves();
    } catch (e) {
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      go({ ...v, busy: false, error: `Not saved. ${messageOf(e)}` });
    }
  }

  async function removeGloves(): Promise<void> {
    if (view.kind !== "gloveRemove" || view.busy || !view.reason) return;
    const v = { ...view, error: "" };
    go({ ...v, busy: true });
    try {
      await callServer<{ ok: true }>("removeGloves", { reason: v.reason, notes: v.notes, forBadgeId: v.data.badgeId });
      touchActivity();
      await backToGloves();
    } catch (e) {
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      go({ ...v, busy: false, error: `Not saved. ${messageOf(e)}` });
    }
  }

  async function openResources(data: MyDashboard, keepTiers = false): Promise<void> {
    if (!keepTiers) openSopTiers.clear();
    go({ kind: "resources", data, lib: null, error: "" });
    try {
      const lib = await callServer<SopLibrary>("loadResources", { forBadgeId: data.badgeId });
      if (view.kind === "resources" && view.data === data) go({ ...view, lib });
    } catch (e) {
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      if (view.kind === "resources" && view.data === data) go({ ...view, error: `Could not load the SOP Library. ${messageOf(e)}` });
    }
  }

  /** Opens one SOP for review: logs the open, then shows the same file with the acknowledgment hidden. */
  async function openSop(data: MyDashboard, lib: SopLibrary, sop: SopEntry): Promise<void> {
    const v: Extract<View, { kind: "sop" }> = { kind: "sop", data, lib, sop, frameUrl: null, error: "" };
    go(v);
    try {
      const [, html] = await Promise.all([
        callServer<{ ok: true }>("logSopOpen", { sopNumber: sop.sopNumber, from: "Resources", forBadgeId: data.badgeId }),
        fetch(sop.fileUrl).then((r) => {
          if (!r.ok) throw new Error(`${sop.sopNumber} did not load.`);
          return r.text();
        }),
      ]);
      touchActivity();
      if (view.kind !== "sop" || view.sop !== sop) return;
      const url = URL.createObjectURL(new Blob([reviewHtml(html)], { type: "text/html" }));
      view = { ...v, frameUrl: url };
      sopFrameUrl = url;
      draw();
    } catch (e) {
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      if (view.kind === "sop" && view.sop === sop) go({ ...v, error: `Could not open the SOP. ${messageOf(e)}` });
    }
  }

  // ---- Asset page and Pick a Task ----

  /** One tap on the close card (energized jobs). Closes the JSP, or fills in his answer after the app closed it. */
  async function sendCard(leftAs: string, note: string): Promise<void> {
    if (view.kind !== "dashboard" || !view.data?.closeCard || cardBusy) return;
    const d = view.data;
    const card = d.closeCard!;
    cardBusy = true;
    cardError = "";
    draw();
    try {
      await callServer("closeJsp", { jspNumber: card.jspNumber, card: true, leftAs, note });
      cardThanks = `Thanks. JSP #${card.jspNumber.replace("JSP-", "")} closed.`;
      cardNoteOpen = false;
      if (view.kind === "dashboard" && view.data === d) go({ kind: "dashboard", data: { ...d, closeCard: null }, error: "" });
    } catch (e) {
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      cardError = `It did not save. ${messageOf(e)}`;
    } finally {
      cardBusy = false;
      draw();
    }
  }

  /** De-energized job on the card: open the JSP at the close pill, at its asset's QEW page. */
  function openCardJsp(card: CloseCard): void {
    try {
      sessionStorage.setItem(CLOSE_REQUEST_KEY, card.jspNumber);
    } catch {
      /* storage blocked: he lands on the QEW page and taps Close JSP there */
    }
    if (fromAsset && fromAsset.assetId === card.assetId) void openWork();
    else location.assign(`/?aid=${encodeURIComponent(card.assetId)}#qew`);
  }

  /** Item 5: Close JSP #n. His submitted JSP opens on the yellow page with the close pill. */
  function reopenJsp(work: AssetWork): void {
    const oj = work.openJsp;
    if (!oj) return;
    touchActivity();
    go({ kind: "jsp", work, taskId: oj.taskId, jspNumber: oj.jspNumber, draft: oj.draft, reopen: { autoAt: oj.autoAt } });
  }

  /** The QEW working page for the asset he scanned. */
  async function openWork(taskId = "", note = ""): Promise<void> {
    if (!fromAsset) {
      await openDashboard();
      return;
    }
    go({ kind: "work", work: null, taskId, note: "", error: "" });
    watchIdle();
    try {
      const work = await callServer<AssetWork>("loadAssetWork", { assetId: fromAsset.assetId, company });
      document.title = `${work.asset.assetId} · SafeTag`;
      // Item 5: the Dashboard card sent him here to close this JSP. Open it at the close pill.
      const wanted = takeCloseRequest();
      if (view.kind === "work" && wanted && work.openJsp?.jspNumber === wanted) {
        go({ kind: "work", work, taskId, note, error: "" });
        reopenJsp(work);
        return;
      }
      if (view.kind === "work") go({ kind: "work", work, taskId, note, error: "" });
      window.scrollTo(0, 0);
    } catch (e) {
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      if (view.kind === "work") go({ kind: "work", work: null, taskId, note: "", error: messageOf(e) });
    }
  }

  /** Back to the working page with the same pick, at the top of the page. */
  function backToWork(work: AssetWork, taskId: string, note: string): void {
    go({ kind: "work", work, taskId, note, error: "" });
    window.scrollTo(0, 0);
  }

  /**
   * Start JSP. A draft for this task resumes. A draft for another task asks Discard first.
   * Otherwise the server checks the pick and reserves the number, then the JSP opens.
   */
  let startingJsp = false;
  async function startJsp(work: AssetWork, taskId: string): Promise<void> {
    if (startingJsp) return;
    const d = work.draft;
    if (d && d.taskId === taskId) {
      touchActivity();
      go({ kind: "jsp", work, taskId, jspNumber: d.jspNumber, draft: d.draft });
      return;
    }
    if (d) {
      go({ kind: "work", work, taskId, note: "", error: "", confirmDiscard: true });
      return;
    }
    startingJsp = true;
    go({ kind: "work", work, taskId, note: "Starting the JSP…", error: "" });
    try {
      const res = await callServer<{ jspNumber: string }>("startJsp", { assetId: work.asset.assetId, taskId, forBadgeId: work.qew.badgeId, company });
      touchActivity();
      if (view.kind === "work" && view.work === work) go({ kind: "jsp", work, taskId, jspNumber: res.jspNumber, draft: null });
    } catch (e) {
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      if (view.kind === "work" && view.work === work) go({ kind: "work", work, taskId, note: `The JSP did not start. ${messageOf(e)}`, error: "" });
    } finally {
      startingJsp = false;
    }
  }

  /** Cancel JSP before Submit, from the QEW page. The draft stays on record (the discard path), then Start opens again. */
  async function cancelDraft(work: AssetWork, taskId: string): Promise<void> {
    const d = work.draft;
    if (!d || startingJsp) return;
    const n = d.jspNumber.replace("JSP-", "");
    startingJsp = true;
    go({ kind: "work", work, taskId, note: `Cancelling JSP #${n}…`, error: "" });
    try {
      await callServer("discardJsp", { jspNumber: d.jspNumber, forBadgeId: work.qew.badgeId, company });
    } catch (e) {
      startingJsp = false;
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      if (view.kind === "work" && view.work === work) go({ kind: "work", work, taskId, note: `JSP #${n} was not cancelled. ${messageOf(e)}`, error: "" });
      return;
    }
    startingJsp = false;
    if (view.kind === "work" && view.work === work) await openWork(taskId, `JSP #${n} cancelled.`);
  }

  /** Discard And Start: the old draft stays on record as discarded, then the new JSP starts. */
  async function discardAndStart(work: AssetWork, taskId: string): Promise<void> {
    const d = work.draft;
    if (!d || startingJsp) return;
    startingJsp = true;
    go({ kind: "work", work, taskId, note: `Discarding JSP #${d.jspNumber.replace("JSP-", "")}…`, error: "" });
    try {
      await callServer("discardJsp", { jspNumber: d.jspNumber, forBadgeId: work.qew.badgeId, company });
    } catch (e) {
      startingJsp = false;
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      if (view.kind === "work" && view.work === work) go({ kind: "work", work, taskId, note: `JSP #${d.jspNumber.replace("JSP-", "")} was not discarded. ${messageOf(e)}`, error: "" });
      return;
    }
    startingJsp = false;
    const next = { ...work, draft: null };
    if (view.kind === "work" && view.work === work) await startJsp(next, taskId);
  }

  /**
   * Opens the task's SOP. To qualify: the same file as Resources, with its own timer and acknowledgment.
   * Already qualified or practiced: a reference read, with the timer banner and acknowledgment hidden.
   */
  async function openSopRead(work: AssetWork, taskId: string, reference = false): Promise<void> {
    const task = work.tasks.find((t) => t.taskId === taskId);
    const sop = sopFor(work, task);
    if (!task || !sop) return;
    const v: Extract<View, { kind: "sopRead" }> = { kind: "sopRead", work, taskId, task, sop, frameUrl: null, busy: false, error: "", ack: null, reference };
    go(v);
    window.scrollTo(0, 0);
    // Every open is logged. It never blocks the read.
    void callServer("logSopOpen", { sopNumber: sop.sopNumber, from: "Asset", assetId: work.asset.assetId, forBadgeId: work.qew.badgeId }).catch(() => undefined);
    try {
      const r = await fetch(sop.fileUrl);
      if (!r.ok) throw new Error(`${sop.sopNumber} did not load.`);
      const html = await r.text();
      touchActivity();
      if (view.kind !== "sopRead" || view.sop !== sop) return;
      const url = URL.createObjectURL(new Blob([reference ? reviewHtml(html) : qualifyingHtml(html)], { type: "text/html" }));
      sopFrameUrl = url;
      go({ ...v, frameUrl: url });
    } catch (e) {
      if (view.kind === "sopRead" && view.sop === sop) go({ ...v, error: `Could not open the SOP. ${messageOf(e)}` });
    }
  }

  /** Records the acknowledgment. A real asset authorizes the task; a demo asset is practice. */
  async function sendAck(v: Extract<View, { kind: "sopRead" }>, ack: SopAck): Promise<void> {
    go({ ...v, busy: true, error: "", ack });
    try {
      const res = await callServer<{ practice: boolean; authorization: { taskId: string; lastSopReviewOn: string | null; lastPerformedOn: string | null } | null }>(
        "confirmTaskSop",
        { assetId: v.work.asset.assetId, taskId: v.taskId, ack: sopAckPayload(ack), forBadgeId: v.work.qew.badgeId, company },
      );
      touchActivity();
      let work = v.work;
      if (res.practice) practiced.add(v.taskId);
      else if (res.authorization) {
        const auth = res.authorization;
        work = { ...work, taskAuthorizations: [...work.taskAuthorizations.filter((a) => a.taskId !== auth.taskId), auth] };
      }
      backToWork(work, v.taskId, "");
    } catch (e) {
      if (messageOf(e) === OTHER_SIGN_IN) return otherSignIn();
      if (view.kind === "sopRead") go({ ...view, busy: false, error: `Your review was not recorded. ${messageOf(e)}`, ack });
    }
  }

  // The SOP file posts its acknowledgment to this page. Only the open SOP frame counts.
  window.addEventListener("message", (ev) => {
    if (view.kind !== "sopRead" || view.reference || view.busy || view.error || !host.isConnected) return;
    const frame = host.querySelector<HTMLIFrameElement>("#aw-sop");
    if (!frame || ev.source !== frame.contentWindow) return;
    const ack = sopAckOf(ev.data);
    if (ack) void sendAck(view, ack);
  });

  /** The QEW this page shows, while one is showing. */
  function pageBadgeId(): string | null {
    if ((view.kind === "work" || view.kind === "sopRead" || view.kind === "jsp") && view.work) return view.work.qew.badgeId;
    return "data" in view && view.data ? view.data.badgeId : null;
  }

  /** Another QEW signed in on this phone. Nothing is saved; this page closes to the gate. */
  function otherSignIn(note = OTHER_SIGN_IN): void {
    stopIdle();
    go({ kind: "gate", note, scanning: false });
  }

  /** Coming back to this tab: if the phone is now signed in as someone else, close this page. */
  async function checkSameQew(): Promise<void> {
    const shown = pageBadgeId();
    if (!shown) return;
    const now = await currentBadge().catch(() => null);
    if (!now || !isQewSignIn(now)) otherSignIn("Signed out. Scan your bag tag again.");
    else if (now.badgeId !== shown) otherSignIn();
  }

  async function signOutTo(note: string): Promise<void> {
    stopIdle();
    await signOutNow().catch(() => undefined);
    go({ kind: "gate", note, scanning: false });
  }

  function toAdmin(): void {
    // Admin tag and PIN come later. Until then the admin signs in with email on the admin pages.
    stopCamera();
    try {
      sessionStorage.setItem(ADMIN_REQUEST_KEY, "1");
      if (location.search.startsWith("?")) sessionStorage.setItem(ADMIN_RETURN_KEY, `/${location.search}${location.hash}`);
    } catch {
      /* storage blocked: the root page may show the QEW Dashboard; Sign Out there reaches the admin sign-in */
    }
    location.href = "/";
  }

  // ---- 1 hour idle ----

  function watchIdle(): void {
    stopIdle();
    idleTimer = window.setInterval(() => {
      if (!host.isConnected) return stopIdle();
      if (isSessionExpired(readActivity(), new Date())) void signOutTo("Signed out after 1 hour with no activity.");
    }, 30_000);
  }

  function stopIdle(): void {
    if (idleTimer !== null) window.clearInterval(idleTimer);
    idleTimer = null;
  }

  let lastTouch = 0;
  const onActivity = () => {
    if (view.kind !== "dashboard" && view.kind !== "loto" && view.kind !== "resources" && view.kind !== "sop" && view.kind !== "work" && view.kind !== "sopRead" && view.kind !== "jsp" && !view.kind.startsWith("glove")) return;
    const now = Date.now();
    if (now - lastTouch > 15_000) {
      lastTouch = now;
      touchActivity();
    }
  };
  host.addEventListener("pointerdown", onActivity);
  // Glove fields keep their values across redraws. A finished date redraws so the next step can appear.
  host.addEventListener("input", (ev) => {
    const el = ev.target as HTMLInputElement;
    if (view.kind === "gloveArrive" && (el.id === "gl-tested" || el.id === "gl-firstuse")) {
      if (el.id === "gl-tested") view.testedOn = el.value;
      else view.firstUse = el.value;
      refreshArrival(view);
    }
    if (view.kind === "gloveRemove" && el.id === "gl-notes") view.notes = el.value;
  });
  host.addEventListener("change", (ev) => {
    const el = ev.target as HTMLInputElement;
    if (view.kind === "work" && el.id === "aw-task") {
      touchActivity();
      go({ ...view, taskId: el.value, note: "", confirmDiscard: false, confirmCancel: false });
      return;
    }
    if (view.kind === "gloveArrive" && (el.id === "gl-tested" || el.id === "gl-firstuse")) {
      if (el.id === "gl-tested") view.testedOn = el.value;
      else view.firstUse = el.value;
      refreshArrival(view);
      return;
    }
    if (el.id !== "fa-loto-none") return;
    const date = host.querySelector<HTMLInputElement>("#fa-loto-date");
    if (date) date.disabled = el.checked;
  });
  host.addEventListener("keydown", onActivity);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && host.isConnected) void checkSameQew();
  });

  // ---- Events ----

  host.addEventListener("click", (ev) => {
    const el = (ev.target as HTMLElement).closest<HTMLElement>("[data-fa], [data-fa-key]");
    if (!el || (el as HTMLButtonElement).disabled) return;
    const key = el.dataset.faKey;
    if (key !== undefined && view.kind === "typed") {
      if (view.digits.length < 4) go({ kind: "typed", digits: view.digits + key });
      return;
    }
    switch (el.dataset.fa) {
      case "cancel":
        stopCamera();
        if (fromAsset) toGuestPage();
        else go({ kind: "gate", note: "", scanning: false });
        return;
      case "wait-cancel":
        waitTicket++;
        if (fromAsset) toGuestPage();
        else go({ kind: "gate", note: "", scanning: false });
        return;
      case "guest":
        toGuestPage();
        return;
      case "gate":
        go({ kind: "gate", note: "", scanning: false });
        return;
      case "scan":
        if (view.kind === "gate") go({ ...view, scanning: true });
        return;
      case "scan-stop":
        if (view.kind === "gate") go({ ...view, scanning: false });
        return;
      case "type":
        go({ kind: "typed", digits: "" });
        return;
      case "del":
        if (view.kind === "typed") go({ kind: "typed", digits: view.digits.slice(0, -1) });
        return;
      case "typed-next": {
        const id = view.kind === "typed" ? typedQewId(view.digits) : null;
        if (id) void checkTag(id);
        return;
      }
      case "admin":
        toAdmin();
        return;
      case "work":
      case "reload-work":
        void openWork();
        return;
      case "dash":
        void openDashboard();
        return;
      case "review":
        if (view.kind === "work" && view.work) void openSopRead(view.work, view.taskId, cardFor(view.work, view.taskId, practiced)?.reviewSmall === true);
        return;
      case "sopread-cancel":
        if (view.kind === "sopRead") backToWork(view.work, view.taskId, "");
        return;
      case "ack-retry":
        if (view.kind === "sopRead" && view.ack) void sendAck(view, view.ack);
        else if (view.kind === "sopRead") void openSopRead(view.work, view.taskId, view.reference);
        return;
      case "start":
        if (view.kind === "work" && view.work) void startJsp(view.work, view.taskId);
        return;
      case "closejsp":
        if (view.kind === "work" && view.work) reopenJsp(view.work);
        return;
      case "card-left":
        if (view.kind === "dashboard" && view.data) void sendCard(el.dataset.left ?? "", "");
        return;
      case "card-worse":
        cardNoteOpen = true;
        draw();
        return;
      case "card-cancel":
        cardNoteOpen = false;
        cardError = "";
        draw();
        return;
      case "card-send":
        void sendCard("worse", host.querySelector<HTMLTextAreaElement>("#fa-card-note")?.value ?? "");
        return;
      case "ask-read": {
        const id = el.dataset.permit ?? "";
        askRead[id] = !askRead[id];
        draw();
        return;
      }
      case "ask-yes":
        void answerAsk(el.dataset.permit ?? "", true);
        return;
      case "ask-no":
        void answerAsk(el.dataset.permit ?? "", false);
        return;
      case "card-open":
        if (view.kind === "dashboard" && view.data?.closeCard) openCardJsp(view.data.closeCard);
        return;
      case "canceljsp":
        if (view.kind === "work") go({ ...view, confirmCancel: true, confirmDiscard: false });
        return;
      case "keepjsp":
        if (view.kind === "work") go({ ...view, confirmCancel: false });
        return;
      case "cancelconfirm":
        if (view.kind === "work" && view.work) void cancelDraft(view.work, view.taskId);
        return;
      case "keepdraft":
        if (view.kind === "work") go({ ...view, confirmDiscard: false });
        return;
      case "discardstart":
        if (view.kind === "work" && view.work) void discardAndStart(view.work, view.taskId);
        return;
      case "reload-dash":
        void openDashboard();
        return;
      case "signout":
        void signOutTo("Signed out.");
        return;
      case "loto":
        if (view.kind === "dashboard" && view.data) {
          const d = view.data;
          go({ kind: "loto", data: d, date: d.lotoNoneReported ? "" : (d.lotoTrainedOn ?? ""), none: d.lotoNoneReported, error: "", busy: false });
        }
        return;
      case "loto-cancel":
        if (view.kind === "loto") go({ kind: "dashboard", data: view.data, error: "" });
        return;
      case "tier": {
        const t = Number(el.dataset.tier);
        if (view.kind !== "dashboard" || !view.data || !t) return;
        if (openTiers.has(t)) openTiers.delete(t);
        else openTiers.add(t);
        const y = window.scrollY;
        draw();
        window.scrollTo(0, y);
        return;
      }
      case "gloves":
        if (view.kind === "dashboard" && view.data) go(gloveCard(view.data));
        return;
      case "glove-back":
        if (view.kind === "gloves") go({ kind: "dashboard", data: view.data, error: "" });
        return;
      case "glove-size":
        if (view.kind === "gloves") go({ ...view, pickSize: el.dataset.size ?? "" });
        return;
      case "glove-size-save":
        if (view.kind === "gloves") void gloveStep("setGloveSize", { gloveSize: view.pickSize });
        return;
      case "glove-size-change":
        if (view.kind === "gloves") go({ ...view, editingSize: true, pickSize: view.data.gloveSize ?? "" });
        return;
      case "glove-size-cancel":
        if (view.kind === "gloves") go({ ...view, editingSize: false, error: "" });
        return;
      case "glove-place":
        void gloveStep("placeGlovesInService", {});
        return;
      case "glove-arrived":
        if (view.kind === "gloves") {
          const d = view.data;
          go({ kind: "gloveArrive", data: d, replacing: d.gloves?.pairId ?? null, sealed: null, testedOn: "", firstUseMode: null, firstUse: "", error: "", busy: false });
        }
        return;
      case "glove-sealed":
        if (view.kind === "gloveArrive") go({ ...view, sealed: el.dataset.v === "yes", error: "" });
        return;
      case "glove-step-back":
        if (view.kind === "gloveArrive") go({ ...view, sealed: null, testedOn: "", firstUseMode: null, firstUse: "", error: "" });
        return;
      case "glove-first":
        if (view.kind === "gloveArrive") go({ ...view, firstUseMode: el.dataset.v === "earlier" ? "earlier" : "today", error: "" });
        return;
      case "glove-arrive-save":
        void saveArrival();
        return;
      case "glove-remove":
        if (view.kind === "gloves") go({ kind: "gloveRemove", data: view.data, reason: "", notes: "", error: "", busy: false });
        return;
      case "glove-cancel":
        if (view.kind === "gloveArrive" || view.kind === "gloveRemove") go(gloveCard(view.data));
        return;
      case "glove-reason":
        if (view.kind === "gloveRemove") go({ ...view, reason: el.dataset.reason ?? "" });
        return;
      case "glove-remove-go":
        void removeGloves();
        return;
      case "loto-send":
        void sendLoto();
        return;
      case "resources":
        if ((view.kind === "dashboard" || view.kind === "resources") && view.data) void openResources(view.data);
        return;
      case "res-back":
        if (view.kind === "resources") go({ kind: "dashboard", data: view.data, error: "" });
        return;
      case "sop-tier": {
        const t = Number(el.dataset.tier);
        if (view.kind !== "resources" || !t) return;
        if (openSopTiers.has(t)) openSopTiers.delete(t);
        else openSopTiers.add(t);
        const y = window.scrollY;
        draw();
        window.scrollTo(0, y);
        return;
      }
      case "sop": {
        if (view.kind !== "resources" || !view.lib) return;
        const sop = findSop(view.lib, el.dataset.sop ?? "");
        if (sop) void openSop(view.data, view.lib, sop);
        return;
      }
      case "sop-retry":
        if (view.kind === "sop") void openSop(view.data, view.lib, view.sop);
        return;
      case "sop-close":
        if (view.kind === "sop") go({ kind: "resources", data: view.data, lib: view.lib, error: "" });
        return;
      default:
        return;
    }
  });

  // ---- First screen ----

  /** A refresh with the visit still open: back to the same page (approved 2026-10-09). */
  async function openStart(p: Page): Promise<void> {
    if (p === "qew" && fromAsset) {
      restoring = false;
      await openWork();
      return;
    }
    if (p === "dashboard" || p === "qew") {
      stack = chainOf("dashboard", Boolean(fromAsset));
      restoring = false;
      await openDashboard();
      return;
    }
    const d = await openDashboard();
    restoring = false;
    if (!d) {
      stack = chainOf("dashboard", Boolean(fromAsset));
      syncHistory();
      return;
    }
    if (p === "gloves") go(gloveCard(d));
    else await openResources(d);
  }

  /** No visit to restore: start fresh at this entry. */
  function noRestore(): void {
    if (!restoring) return;
    restoring = false;
    stack = fromAsset ? ["guest"] : [];
    history.replaceState(null, "", baseUrl);
  }

  void (async () => {
    const visit = await openQewVisit();
    if (visit && startPage) return openStart(startPage);
    noRestore();
    if (start.kind === "dashboard") {
      if (visit) await openDashboard();
      else go({ kind: "gate", note: "", scanning: false });
      return;
    }
    if (start.kind === "tag") {
      const check = nameCheckOf(start.view);
      // Same QEW, visit still open: no second name check.
      if (visit && check.kind === "ask" && visit.badgeId === check.tagId) {
        await openDashboard();
        return;
      }
      if (check.kind === "ask") {
        if (check.siteName) siteTitle = `Alltech — ${check.siteName}`;
        await confirmYes(check.tagId);
      } else go({ kind: "stop", message: check.message, tagId: check.tagId, unclaimed: check.kind === "unclaimed" });
      return;
    }
    // From an asset: an open visit skips the gate and opens the working page.
    if (visit) await openWork();
    else go({ kind: "gate", note: "", scanning: false });
  })();
}
